import { lazy } from 'react';

// Split out of the storefront bundle (#469). Checkout is prefetched once the
// home page is idle; Admin is only ever fetched by the two people who use it.
export const loadCheckout = () => import('pages/Checkout');
export const loadAdmin = () => import('pages/Admin');

const RELOAD_FLAG = 'chunk-reload-attempted';

// True the first time it is asked in this tab, false after that. Without
// sessionStorage there is no way to stop a reload loop, so it answers false.
const claimReload = (win) => {
  try {
    if (win.sessionStorage.getItem(RELOAD_FLAG) === '1') return false;
    win.sessionStorage.setItem(RELOAD_FLAG, '1');
    return true;
  } catch {
    return false;
  }
};

const clearReload = (win) => {
  try {
    win.sessionStorage.removeItem(RELOAD_FLAG);
  } catch {
    // nothing to clear
  }
};

/**
 * Wrap a page import for React.lazy so a chunk that fails to load reloads the
 * page once. A deploy renames every chunk, so a tab opened before it can ask
 * for a file that no longer exists; a phone can also lose its connection.
 *
 * During the reload the promise stays pending, which keeps the Suspense
 * fallback on screen. Resolving with nothing makes React.lazy throw (#630).
 * If the reload has already been tried, it rejects with `isChunkLoadError` so
 * the error boundary can offer a retry and not report a crash.
 *
 * @param {() => Promise<{default: Function}>} importer
 * @param {Window} [win]
 * @returns {() => Promise<{default: Function}>}
 */
export const loadWithReload =
  (importer, win = window) =>
  () =>
    importer()
      .then((module) => {
        if (!module) throw new Error('The page chunk loaded empty');
        clearReload(win);
        return module;
      })
      .catch((cause) => {
        if (claimReload(win)) {
          win.location.reload();
          return new Promise(() => {});
        }
        throw Object.assign(new Error('Could not load this page', { cause }), {
          isChunkLoadError: true,
        });
      });

export const Checkout = lazy(loadWithReload(loadCheckout));
export const Admin = lazy(loadWithReload(loadAdmin));

export const prefetchWhenIdle = (load, win = window) => {
  const fetchChunk = () => {
    // A failed prefetch costs nothing: the route fetches again when visited.
    load().catch(() => {});
  };
  const whenIdle = () => {
    if (typeof win.requestIdleCallback === 'function') {
      win.requestIdleCallback(fetchChunk, { timeout: 5000 });
    } else {
      win.setTimeout(fetchChunk, 2000);
    }
  };

  if (win.document.readyState === 'complete') {
    whenIdle();
  } else {
    win.addEventListener('load', whenIdle, { once: true });
  }
};
